import React, { useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import type { DateRange, DayButtonProps } from 'react-day-picker';
import { fr } from 'react-day-picker/locale';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

interface Props {
  accommodationId: string;
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  /** Primary: set check-in (and clear / start a new range). */
  onSelectCheckIn: (date: Date | undefined) => void;
  /** When both ends of a range are chosen. */
  onSelectRange?: (from: Date | undefined, to: Date | undefined) => void;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isInRange(date: Date, startStr: string, endStr: string): boolean {
  const d = isoDate(date);
  return d >= startStr && d < endStr;
}

export function AvailabilityDayPicker({
  accommodationId,
  checkIn,
  checkOut,
  onSelectCheckIn,
  onSelectRange,
}: Props) {
  const unavailable = useQuery(api.temporaryReservations.getUnavailableDateRanges, {
    accommodationId: accommodationId as Id<'accommodations'>,
  });

  const now = Date.now();

  const isBooked = useMemo(
    () => (date: Date) =>
      unavailable?.booked.some((r) => isInRange(date, r.startDate, r.endDate)) ?? false,
    [unavailable],
  );

  const isLocked = useMemo(
    () => (date: Date) =>
      unavailable?.locked.some((r) => r.expiresAt > now && isInRange(date, r.startDate, r.endDate)) ??
      false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unavailable],
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selected: DateRange | undefined = checkIn
    ? { from: checkIn, to: checkOut && checkOut > checkIn ? checkOut : undefined }
    : undefined;

  function CustomDayButton({ day, modifiers, ...buttonProps }: DayButtonProps) {
    const d = day.date;
    const isPast = d < today;
    const booked = !isPast && isBooked(d);
    const locked = !isPast && !booked && isLocked(d);
    const dotColor = booked ? '#ef4444' : locked ? '#c9a77b' : '#2f9e44';
    const isStart = Boolean(modifiers.range_start);
    const isEnd = Boolean(modifiers.range_end);
    const isMiddle = Boolean(modifiers.range_middle);
    const isSelected = Boolean(modifiers.selected) || isStart || isEnd;

    return (
      <div className="relative flex flex-col items-center">
        <button
          {...buttonProps}
          className={[
            'flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors',
            isPast || booked
              ? 'cursor-not-allowed text-[#3d2817]/30'
              : 'text-[#3d2817] hover:bg-[#2f9e44]/15',
            isSelected || isStart || isEnd ? 'bg-[#2f9e44] text-white hover:bg-[#27863a]' : '',
            isMiddle && !isStart && !isEnd ? 'rounded-none bg-[#2f9e44]/15 text-[#3d2817]' : '',
            locked && !isSelected ? 'opacity-70' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={isPast || booked || buttonProps.disabled}
        />
        {!isPast && (
          <span
            className="mt-0.5 block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: isSelected ? '#fff' : dotColor }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#3d2817]/12 bg-white/90 shadow-sm">
      <div className="flex flex-wrap items-center justify-center gap-3 border-b border-[#3d2817]/08 px-3 py-2.5 text-[11px] text-[#5d4437]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[#2f9e44]" />
          Disponible
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[#c9a77b]" />
          Temp. réservé
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[#ef4444]" />
          Non disponible
        </span>
      </div>

      <div className="guest-avail-daypicker px-2 pb-3 pt-1">
        <DayPicker
          locale={fr}
          mode="range"
          selected={selected}
          onSelect={(range) => {
            if (!range?.from) {
              onSelectCheckIn(undefined);
              onSelectRange?.(undefined, undefined);
              return;
            }
            if (range.to && range.to.getTime() !== range.from.getTime()) {
              onSelectRange?.(range.from, range.to);
              return;
            }
            onSelectCheckIn(range.from);
          }}
          disabled={[
            { before: today },
            (date) => isBooked(date),
          ]}
          startMonth={today}
          components={{ DayButton: CustomDayButton }}
          classNames={{
            root: 'rdp-guest',
            months: 'flex flex-col',
            month: 'space-y-2',
            month_caption: 'flex items-center justify-center py-2',
            caption_label: 'font-felix-titling text-sm font-medium uppercase tracking-wide text-[#3d2817]',
            nav: 'flex items-center justify-between px-1',
            button_previous:
              'inline-flex h-8 w-8 items-center justify-center rounded-full text-[#3d2817] hover:bg-[#2f9e44]/12',
            button_next:
              'inline-flex h-8 w-8 items-center justify-center rounded-full text-[#3d2817] hover:bg-[#2f9e44]/12',
            weekdays: 'grid grid-cols-7 gap-0',
            weekday: 'text-center text-[10px] font-semibold uppercase text-[#3d2817]/45',
            week: 'mt-1 grid grid-cols-7 justify-items-center gap-y-1',
            day: 'flex items-center justify-center p-0',
            selected: '',
            range_start: '',
            range_end: '',
            range_middle: '',
            today: 'font-bold',
            disabled: 'opacity-40',
            outside: 'opacity-30',
          }}
        />
      </div>
    </div>
  );
}
